import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import EditIcon from '@/assets/icons/edit.svg';
import { useTrainingGoals } from '@/hooks/useTrainingGoals';
import { ProgressBelt } from './ProgressBelt';
import { TrainingGoalModal } from './TrainingGoalModal';
import { format, addDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';

export const TrainingGoalSection: React.FC = () => {
  const { user } = useAuth();
  const { goal, isLoading, beltColors, checkGoalProgress } = useTrainingGoals();
  const { profile } = useUserProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Check goal progress automatically when component mounts (only once)
  useEffect(() => {
    if (goal && user?.id) {
      checkGoalProgress().catch(error => {
        console.error('❌ Error checking goal progress:', error);
      });
    }
  }, [goal?.id, user?.id]); // Remove checkGoalProgress from dependencies to prevent infinite loop



  // Get user's belt color from profile
  const getUserBeltColor = (): string => {
    if (!profile?.belt_rank) return 'white';
    
    // Map belt_rank to our BeltColor type
    const beltRank = profile.belt_rank.toLowerCase();
    if (beltRank.includes('white')) return 'white';
    if (beltRank.includes('blue')) return 'blue';
    if (beltRank.includes('purple')) return 'purple';
    if (beltRank.includes('brown')) return 'brown';
    if (beltRank.includes('black')) return 'black';
    
    return 'white'; // default
  };

  const handleOpenModal = (editing: boolean = false) => {
    setIsEditing(editing);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
  };

  const getProgressMessage = () => {
    if (!goal) return '';

    const { current_week_progress, weekly_goal, is_monthly_goal_met } = goal;

    if (is_monthly_goal_met) {
      return "Monthly Goal Achieved! Your new stripe is earned. Oss!";
    }

    // ✅ LÓGICA CORRETA: usar o progresso da semana atual (domingo a sábado)
    return `You're at ${current_week_progress}/${weekly_goal} trainings this week. Push for that last roll!`;
  };

  const getWeeklyMessage = () => {
    if (!goal) return '';
    
    // ✅ LÓGICA CORRETA: mostrar progresso da semana atual (domingo a sábado)
    return `Complete ${goal.weekly_goal} trainings this week and earn a new stripe! (${goal.current_week_progress || 0}/${goal.weekly_goal})`;
  };

  const getDateRange = () => {
    if (!goal) return '';
    
    // ✅ LÓGICA CORRETA: mostrar o mês atual (não o ciclo da meta)
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return `${format(monthStart, 'dd MMM')} to ${format(monthEnd, 'dd MMM')}`;
  };

  const weeklyGoalsStatus = useMemo(() => {
    if (!goal) return [false, false, false, false];
    
    // ✅ LÓGICA CORRETA: graus baseados no progresso da semana atual
    const { current_week_progress, weekly_goal, total_completed, monthly_goal } = goal;
    
    // Grau 1: Semana atual completada
    const week1Completed = current_week_progress >= weekly_goal;
    
    // Grau 2: Duas semanas completadas (seguindo o padrão de 4 semanas por mês)
    const week2Completed = total_completed >= weekly_goal * 2;
    
    // Grau 3: Três semanas completadas
    const week3Completed = total_completed >= weekly_goal * 3;
    
    // Grau 4: Meta mensal atingida
    const week4Completed = total_completed >= monthly_goal;
    
    return [week1Completed, week2Completed, week3Completed, week4Completed];
  }, [goal?.current_week_progress, goal?.weekly_goal, goal?.total_completed, goal?.monthly_goal]);

  if (isLoading) {
    return (
      <div className="bg-[#2d2424] rounded-[10px] h-[129px] w-[350px] p-6 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!goal) {
    // Empty State - Following Figma design exactly
    return (
      <>
        <div className="bg-[#2d2424] h-[130px] w-full rounded-lg p-4 flex flex-col items-center justify-center text-center relative">
          {/* Main title - "No goal set yet!" */}
          <h3 className="text-[14px] font-bold text-white font-roboto mb-2">
            No goal set yet!
          </h3>
          
          {/* Subtitle - "Track your progress and earn a new stripe by week completed." */}
          <p className="text-[10px] font-light text-white mb-4 max-w-[287px]">
            Track your progress and earn a new stripe by week completed.
          </p>
          
          {/* Button - "Set goal" with plus icon */}
          <Button
            onClick={() => handleOpenModal(false)}
            className="bg-[#D72E16] hover:bg-[#B91C1C] text-white h-5 px-[30px] py-2 rounded-[100px] text-[10px] font-bold"
          >
            <Plus className="w-3.5 h-3.5 mr-2" />
            Set goal
          </Button>
        </div>

        {/* Modal for empty state */}
        <TrainingGoalModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isEditing={isEditing}
          currentGoal={undefined}
        />
      </>
    );
  }

  // Active State with Progress
  return (
    <div className="w-full">
      {/* Title and Edit Button - Above the rectangle */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-bold text-white">Monthly Progress</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleOpenModal(true)}
            className="text-white/60 hover:text-white"
          >
            <img src={EditIcon} alt="Edit" width={16} height={16} style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>

      {/* Weekly Message - Above the rectangle */}
      <p className="text-[10px] font-light text-white mb-3">
        {getWeeklyMessage()}
      </p>

      {/* Main Progress Container */}
      <div className="bg-[#2d2424] rounded-lg w-full p-4 relative">
        {/* Date Range and Progress Counter */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-light text-white">{getDateRange()}</span>
          <span className="text-[12px] font-normal text-white">
            {goal.total_completed}/{goal.monthly_goal}
          </span>
        </div>

        {/* Progress Belt - Posicionada entre os textos */}
        <div className="flex justify-center mb-3">
          <ProgressBelt
            beltColor={getUserBeltColor() as any}
            progress={goal.total_completed || 0}
            total={goal.monthly_goal || 1}
            weeklyGoals={weeklyGoalsStatus}
            className="w-full max-w-[316px]"
          />
        </div>

        {/* Progress Status Message */}
        <p className="text-[12px] font-normal text-white text-center">
          {getProgressMessage()}
        </p>
      </div>

      {/* Modal */}
      <TrainingGoalModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        isEditing={isEditing}
        currentGoal={goal.monthly_goal}
      />
    </div>
  );
};
